import React, { useState } from 'react';
import {
	TextField,
	Button,
	MenuItem,
	Box,
	Paper,
	Chip,
	IconButton,
	FormControl,
	InputLabel,
	Select,
	SelectChangeEvent,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

interface MenuSectionProps {
	menuItem: {
		menuType: string;
		numberOfPersons: number;
		menuItems: {
			Items: string[];
		};
	};
	onChange: (menuItem: {
		menuType: string;
		numberOfPersons: number;
		menuItems: {
			Items: string[];
		};
	}) => void;
	onRemove: () => void;
	availableMenuTypes: string[];
}

const MenuSection: React.FC<MenuSectionProps> = ({
	menuItem,
	onChange,
	onRemove,
	availableMenuTypes,
}) => {
	console.log(availableMenuTypes, 'meuitem');

	const [currentItem, setCurrentItem] = useState('');

	const handleMenuTypeChange = (e: SelectChangeEvent<string>) => {
		onChange({ ...menuItem, menuType: e.target.value });
	};

	const handleAddItem = () => {
		const trimmed = currentItem.trim();
		if (trimmed) {
			const currentItems = menuItem.menuItems.Items;
			if (!currentItems.includes(trimmed)) {
				onChange({
					...menuItem,
					menuItems: { Items: [...currentItems, trimmed] },
				});
				setCurrentItem('');
			}
		}
	};

	const handleRemoveItem = (item: string) => {
		onChange({
			...menuItem,
			menuItems: {
				Items: menuItem.menuItems.Items.filter((i) => i !== item),
			},
		});
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleAddItem();
		}
	};

	const handleNumberOfPersonsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(e.target.value);
		onChange({
			...menuItem,
			numberOfPersons: isNaN(value) ? 1 : Math.max(1, value),
		});
	};

	const items = menuItem.menuItems.Items;

	return (
		<Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
			<Box mb={3}>
				<FormControl fullWidth>
					<InputLabel>Menu Type *</InputLabel>
					<Select
						value={menuItem.menuType}
						onChange={handleMenuTypeChange}
						label="Menu Type *"
						size="small"
						required
					>
						{availableMenuTypes.map((type, index) => (
							<MenuItem key={index} value={type}>
								{type}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>
			<Box mb={3}>
				<InputLabel>{menuItem.menuType} Items *</InputLabel>
				<Box display="flex" gap={2} mb={2}>
					<TextField
						placeholder="Add menu item"
						value={currentItem}
						onChange={(e) => setCurrentItem(e.target.value)}
						onKeyDown={handleKeyDown}
						size="small"
						fullWidth
					/>
					<Button
						variant="outlined"
						onClick={handleAddItem}
						disabled={!currentItem.trim()}
						sx={{
							'&:hover': {
								backgroundColor: 'primary.main',
								color: 'white',
							},
						}}
					>
						Add
					</Button>
				</Box>
				<Box display="flex" flexWrap="wrap" gap={1} mt={2}>
					{items.map((item) => (
						<Chip
							key={item}
							label={item}
							sx={{ background: '#ffd2d3' }}
							onDelete={() => handleRemoveItem(item)}
							deleteIcon={<CloseIcon style={{ color: '#740003' }} />}
							size="small"
						/>
					))}
					{items.length === 0 && (
						<Typography variant="caption" color="error">
							At least one item is required
						</Typography>
					)}
				</Box>
			</Box>
			<Box
				display="grid"
				gridTemplateColumns="repeat(2, 1fr)"
				gap={2}
				mt={3}
				p={2}
				bgcolor="#f5f5f5"
				borderRadius={1}
			>
				<TextField
					label="Number of Persons *"
					size="small"
					type="number"
					value={menuItem.numberOfPersons || 1}
					onChange={handleNumberOfPersonsChange}
					inputProps={{ min: 1 }}
					required
					error={menuItem.numberOfPersons < 1}
					helperText={menuItem.numberOfPersons < 1 ? 'Must be at least 1' : ''}
				/>
				<Box textAlign="center">
					<InputLabel>Total Items</InputLabel>
					<Typography fontWeight="medium" mt={1}>
						{items.length}
					</Typography>
				</Box>
			</Box>
			<Box display="flex" justifyContent="flex-end">
				<IconButton onClick={onRemove}>
					<DeleteIcon />
				</IconButton>
			</Box>
		</Paper>
	);
};

export default MenuSection;
